import { Injectable } from '@angular/core';

import {
	CoverageMode,
	DeviceDbLevels,
	DeviceModel,
	LobeStyle,
	Placement
} from '@shure/shared/angular/data-access/system-api/models';
import { DeviceBehaviorPluginsService } from '@shure/shared/angular/device-behavior/core';

import { CoveragePerspectives } from '../mxa7xx-lookup-services';

import { DeviceCoverageDeviceBehaviorPlugin } from './device-coverage.device-behavior';

@Injectable({ providedIn: 'root' })
export class DeviceCoverageDeviceBehaviorService {
	constructor(private readonly plugins: DeviceBehaviorPluginsService) {}

	public get3DDeviceLabels(model: DeviceModel): { label: string; position: [number, number, number] }[] {
		return (
			this.plugins.get<DeviceCoverageDeviceBehaviorPlugin>(model, ['get3DDeviceLabels'])?.get3DDeviceLabels?.() ??
			[]
		);
	}

	public get3DDeviceImage(model: DeviceModel): string {
		return (
			this.plugins
				.get<DeviceCoverageDeviceBehaviorPlugin>(model, ['get3DDeviceImage'])
				?.get3DDeviceImage?.(model) ?? ''
		);
	}

	public hasReadOnlyPresets(model: DeviceModel): boolean {
		return (
			this.plugins
				.get<DeviceCoverageDeviceBehaviorPlugin>(model, ['hasReadOnlyPresets'])
				?.hasReadOnlyPresets?.() ?? false
		);
	}

	public usePostGateValues(model: DeviceModel): boolean {
		return (
			this.plugins.get<DeviceCoverageDeviceBehaviorPlugin>(model, ['usePostGateValues'])?.usePostGateValues?.() ??
			false
		);
	}

	public canResetCoverageAreas(model: DeviceModel, coverageMode?: CoverageMode): boolean {
		return (
			this.plugins
				.get<DeviceCoverageDeviceBehaviorPlugin>(model, ['canResetCoverageAreas'])
				?.canResetCoverageAreas?.(coverageMode) ?? false
		);
	}

	public canResetChannels(model: DeviceModel): boolean {
		return (
			this.plugins.get<DeviceCoverageDeviceBehaviorPlugin>(model, ['canResetChannels'])?.canResetChannels?.() ??
			false
		);
	}

	public getDbLevels(model: DeviceModel): DeviceDbLevels {
		return (
			this.plugins.get<DeviceCoverageDeviceBehaviorPlugin>(model, ['getDbLevels'])?.getDbLevels?.() ?? undefined
		);
	}

	public getPlacementLobeStyle(
		model: DeviceModel,
		perspective: CoveragePerspectives,
		placement: Placement
	): LobeStyle {
		return (
			this.plugins
				.get<DeviceCoverageDeviceBehaviorPlugin>(model, ['getPlacementLobeStyle'])
				?.getPlacementLobeStyle?.(perspective, placement) ?? LobeStyle.Wedge
		);
	}

	public getPlacementHasComplimentaryLobes(model: DeviceModel, placement: Placement): boolean {
		return (
			this.plugins
				.get<DeviceCoverageDeviceBehaviorPlugin>(model, ['getPlacementHasComplimentaryLobes'])
				?.getPlacementHasComplimentaryLobes?.(placement) ?? false
		);
	}

	public supports3D(deviceModel: DeviceModel): boolean {
		return (
			this.plugins.get<DeviceCoverageDeviceBehaviorPlugin>(deviceModel, ['supports3D'])?.supports3D?.() ?? false
		);
	}

	public getPlacementLedLayer(placement: Placement, isFlipped: boolean): 'top' | 'bottom' | undefined {
		if (isOfPlacement(placement, [Placement.WallHorizontal, Placement.Table, Placement.Ceiling])) {
			return 'top';
		}
		if (isOfPlacement(placement, [Placement.WallVertical])) {
			return isFlipped ? 'top' : 'bottom';
		}
		return undefined;
	}

	public hasSideView(model: DeviceModel, placement: Placement): boolean {
		return (
			this.plugins.get<DeviceCoverageDeviceBehaviorPlugin>(model, ['hasSideView'])?.hasSideView?.(placement) ??
			false
		);
	}

	public supportsChannels(model: DeviceModel, coverageMode?: CoverageMode): boolean {
		return (
			this.plugins
				.get<DeviceCoverageDeviceBehaviorPlugin>(model, ['supportsChannels'])
				?.supportsChannels?.(coverageMode) ?? false
		);
	}

	public getPlacementOptions(model: DeviceModel): Placement[] | undefined {
		return this.plugins
			.get<DeviceCoverageDeviceBehaviorPlugin>(model, ['getPlacementOptions'])
			?.getPlacementOptions?.();
	}

	public getInstallationRotationDegree(model: DeviceModel, placement: Placement): number | undefined {
		return this.plugins
			.get<DeviceCoverageDeviceBehaviorPlugin>(model, ['getInstallationRotationDegree'])
			?.getInstallationRotationDegree?.(placement);
	}

	public supportsXyzAxisRotation(model: DeviceModel): boolean {
		return (
			this.plugins
				.get<DeviceCoverageDeviceBehaviorPlugin>(model, ['supportsXyzAxisRotation'])
				?.supportsXyzAxisRotation?.() ?? false
		);
	}
}

const isOfPlacement = (placement: Placement | undefined, compare: Placement[]): boolean =>
	!!placement && compare.includes(placement);
